import { FieldErrors } from 'react-hook-form'
import { FieldError } from 'react-hook-form/dist/types/errors'
import { isObject } from 'lodash'
import getValue from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isString from 'lodash/isString'
import map from 'lodash/map'
import union from 'lodash/union'
import uniq from 'lodash/uniq'

export const hasError = <T>(data: T) => !isEmpty(data)

const filterRequiredError = (
	errors: Record<string, FieldErrors[] | FieldErrors>[],
	showRequiredFields: boolean = false
) => {
	return Array.isArray(errors)
		? errors.filter((item: FieldErrors[] | FieldErrors) => {
				// ignore all validations except required
				return showRequiredFields && !Array.isArray(item) && isString(item?.errMsg)
					? item.errMsg.includes('required')
					: true
		  })
		: []
}

export const getFailedValidationFields = (
	errors: FieldErrors,
	labels: { [key: string]: string },
	showRequiredFields: boolean = false
): string[] => {
	const uniqErrors = uniq(
		Object.entries(errors)
			.filter(([key]) =>
				filterRequiredError(errors[key], showRequiredFields).some(
					(item: FieldErrors[] | FieldErrors) => !isEmpty(item)
				)
			)
			.map(([key, value]) => {
				const refLabel =
					Array.isArray(value) &&
					(value as { refLabel?: string }[]).find((item) => item?.refLabel)?.refLabel
				if (refLabel) {
					return labels[refLabel]
				}
				return labels[key]
			})
			.filter((item) => item)
	)
	// sort errors
	return union(
		map(labels).filter((label) => uniqErrors.some((errorLabel) => errorLabel === label))
	)
}

export const getAdditionalValidationFields = (errors: FieldErrors): string[] =>
	Object.values(errors)
		.flat()
		.filter((item) => item.errMsg !== '{field} is required')
		.map(({ errMsg }) => errMsg)

export const getYupFailedValidationFields = (
	errors: FieldErrors | null | undefined,
	labels: { [key: string]: string }
): string[] => {
	const isSupportedYupErrorType = (type?: string) =>
		type === 'required' || type === 'typeError' || type === 'min'
	const pushMappedFieldName = (primaryKey: string, fallbackKey?: string) => {
		const errorFieldName =
			getValue(labels, primaryKey, null) ||
			(fallbackKey ? getValue(labels, fallbackKey, null) : null)
		errorFieldNames.push(errorFieldName)
	}
	const pushIfSupportedType = (type: FieldError['type'], primaryKey: string, fallbackKey?: string) => {
		if (isSupportedYupErrorType(type as string | undefined)) {
			pushMappedFieldName(primaryKey, fallbackKey)
		}
	}

	if (!errors || !isObject(errors)) {
		return []
	}

	const errorFieldNames: Array<string | null> = []
	Object.entries(errors).forEach(([key, value]) => {
		if (Array.isArray(value)) {
			value.forEach((fieldArrayItem) => {
				if (!fieldArrayItem) {
					return
				}

				if (fieldArrayItem?.type === 'email') {
					pushMappedFieldName(key)
				} else {
					Object.entries(fieldArrayItem).forEach(([fieldArrayKey, fieldArrayValue]) => {
						const { type } = fieldArrayValue as FieldError
						pushIfSupportedType(type, fieldArrayKey, key)
					})
				}
			})
		} else if (value && isObject(value)) {
			if ('type' in value) {
				pushIfSupportedType(value.type as FieldError['type'], key)
			}

			if (Object.values(value).every((obj) => isObject(obj) && 'type' in obj)) {
				Object.values(value).forEach((obj) => {
					const { type } = obj as FieldError
					pushIfSupportedType(type, key)
				})
			}
		}
	})
	const uniqErrorFieldNames = uniq(errorFieldNames)
	return union(
		map(labels).filter((label) =>
			uniqErrorFieldNames.some((errorLabel) => errorLabel === label)
		)
	)
}

export const validatePdfFormat = (name: string) => /\.pdf$/.test(name)

export const EMAIL_REGEX = /\w+@\w{2,}\.\w{2,}/
export const URL_REGEX = /\w+\.\w{2,}/

export const URL_ERROR = 'URL_ERROR'
export const EMAIL_ERROR = 'EMAIL_ERROR'

export const validateDocPdfXlsxFile = (name: string) => {
	return /\.(doc|pdf|xlsx)$/.test(name.toLowerCase())
}

export const whitespaceValidationCallback = (value: BasicString) =>
	value?.replaceAll(' ', '') === '' ? null : value
